import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  Modal,
  Row,
  Col,
  Card,
  CardBody,
  Spinner,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Badge,
} from "reactstrap";
import { motion } from "framer-motion";
import FeatherIcon from "feather-icons-react";
import api from "../helpers/api";
import { AssetListTable } from "./Common";
import AssetModal from "./AssetModal";
import { formatCreatedDate } from "../constants/utils";

const STATUS_BADGE = {
  UPCOMING: { color: "info", labelKey: "contracts.status_upcoming", fallback: "Upcoming" },
  ACTIVE: { color: "success", labelKey: "contracts.status_active", fallback: "Active" },
  EXPIRING_SOON: { color: "warning", labelKey: "contracts.status_expiring_soon", fallback: "Expiring Soon" },
  EXPIRED: { color: "danger", labelKey: "contracts.status_expired", fallback: "Expired" },
};

const formatDDMMYYYY = (isoDate) => {
  if (!isoDate) return "";
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
};

/**
 * Detail modal for a single contract. Accepts either a fully-loaded `contract`
 * object or just a `contractId` (then fetches it). Mirrors the old
 * ServiceContractsModal layout: gradient header, two tabs, two-column info.
 */
const ContractDetailModal = ({ isOpen, toggle, contract, contractId, onEdit = null }) => {
  const { t } = useTranslation();
  // One currency per org (no FX conversion) — show the org's current currency, not the
  // per-contract snapshot. See currency-uniformity work.
  const orgCurrency = useSelector((s) => s.ActiveContext?.activeOrganizationCurrency) || "USD";
  const [activeTab, setActiveTab] = useState("info");
  const [data, setData] = useState(contract || null);
  const [loading, setLoading] = useState(false);
  const [assetModal, setAssetModal] = useState({ isOpen: false, userData: null });
  const [linkedAssets, setLinkedAssets] = useState([]);
  const [linkedAssetsLoading, setLinkedAssetsLoading] = useState(false);

  const fetchLinkedAssets = useCallback(async (ids) => {
    if (!ids || ids.length === 0) {
      setLinkedAssets([]);
      return;
    }
    setLinkedAssetsLoading(true);
    try {
      const results = await Promise.all(
        ids.map((id) => api.get(`asset/get-by-id?id=${id}`).then((r) => r.data?.response).catch(() => null))
      );
      setLinkedAssets(results.filter(Boolean));
    } catch (e) {
      setLinkedAssets([]);
    } finally {
      setLinkedAssetsLoading(false);
    }
  }, []);

  const fetchById = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const { data: response } = await api.get(`contract/?id=${id}`);
      setData(response?.response || null);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setActiveTab("info");
    setLinkedAssets([]);
    if (contract) {
      setData(contract);
    } else if (contractId) {
      fetchById(contractId);
    }
  }, [isOpen, contract, contractId, fetchById]);

  // Fetch full asset records lazily when the user opens the Assets tab. The backend's
  // AssetSummary embedded in ContractResponse covers the visible columns, but the
  // AssetModal opened from a click expects the full Assets payload.
  useEffect(() => {
    if (activeTab === "assets" && data?.assetIds?.length && linkedAssets.length === 0) {
      fetchLinkedAssets(data.assetIds);
    }
  }, [activeTab, data?.assetIds, linkedAssets.length, fetchLinkedAssets]);

  const badge = data?.status ? STATUS_BADGE[data.status] || STATUS_BADGE.ACTIVE : null;
  const assetsCount = data?.assets?.length || data?.assetIds?.length || 0;

  return (
    <Modal size="xl" isOpen={isOpen} toggle={toggle}>
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        {/* Header */}
        <div
          className="modal-header border-0 text-white position-sticky"
          style={{
            top: 0,
            zIndex: 10,
            background: "linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)",
            padding: "1.5rem",
          }}
        >
          <div className="d-flex align-items-center">
            <div
              className="bg-white bg-opacity-20 rounded-circle d-flex align-items-center justify-content-center me-3"
              style={{ width: "40px", height: "40px" }}
            >
              <i className="bx bx-file text-white fs-5"></i>
            </div>
            <div>
              <h5 className="modal-title mb-0 fw-bold text-white">
                {t("contracts.detail_modal.title", "Contract Details")}
              </h5>
              <div className="d-flex align-items-center gap-2 mt-1">
                {data?.contractNumber && (
                  <span
                    className="badge text-white"
                    style={{ background: "rgba(255,255,255,0.2)" }}
                  >
                    <i className="bx bx-file me-1"></i>
                    {data.contractNumber}
                  </span>
                )}
                {badge && (
                  <Badge color={badge.color}>{t(badge.labelKey, badge.fallback)}</Badge>
                )}
              </div>
            </div>
          </div>
          <div className="d-flex align-items-center gap-3 ms-auto">
            {onEdit && data && (
              <button
                type="button"
                className="btn btn-sm d-flex align-items-center gap-2 px-3 py-2 border-0 shadow-sm"
                onClick={() => onEdit(data)}
                title={t("contracts.edit_contract", "Edit Contract")}
                style={{
                  background: "rgba(255, 255, 255, 0.95)",
                  color: "#495057",
                  fontWeight: "500",
                  borderRadius: "8px",
                }}
              >
                <FeatherIcon icon="edit-3" size={16} />
                <span>{t("contracts.edit_contract", "Edit Contract")}</span>
              </button>
            )}
            <button
              onClick={toggle}
              type="button"
              className="btn-close btn-close-white"
              aria-label={t("shared.close", "Close")}
            />
          </div>
        </div>

        {/* Tabs */}
        <Nav tabs className="border-bottom-0 px-4 pt-3 bg-light" style={{ borderRadius: "0" }}>
          <NavItem>
            <NavLink
              className={activeTab === "info" ? "active" : ""}
              onClick={() => setActiveTab("info")}
              style={{
                border: "none",
                borderRadius: "8px 8px 0 0",
                backgroundColor: activeTab === "info" ? "#fff" : "transparent",
                color: activeTab === "info" ? "#495057" : "#6c757d",
                fontWeight: activeTab === "info" ? "600" : "400",
                cursor: "pointer",
              }}
            >
              <i className="bx bx-info-circle me-2"></i>
              {t("contracts.detail_modal.tab_info", "Contract Details")}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={activeTab === "assets" ? "active" : ""}
              onClick={() => setActiveTab("assets")}
              style={{
                border: "none",
                borderRadius: "8px 8px 0 0",
                backgroundColor: activeTab === "assets" ? "#fff" : "transparent",
                color: activeTab === "assets" ? "#495057" : "#6c757d",
                fontWeight: activeTab === "assets" ? "600" : "400",
                cursor: "pointer",
              }}
            >
              <i className="bx bx-package me-2"></i>
              {t("contracts.detail_modal.tab_assets", "Associated Assets")}
              {assetsCount > 0 && (
                <Badge color="info" className="ms-2" pill>
                  {assetsCount}
                </Badge>
              )}
            </NavLink>
          </NavItem>
        </Nav>

        {/* Body */}
        <div className="modal-body p-0" style={{ maxHeight: "calc(90vh - 200px)", overflow: "auto" }}>
          <TabContent activeTab={activeTab} className="h-100">
            {/* Info tab */}
            <TabPane tabId="info">
              <div className="p-4">
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                    <p className="text-muted mt-3">
                      {t("contracts.detail_modal.loading", "Loading contract...")}
                    </p>
                  </div>
                ) : !data ? (
                  <div className="text-center py-5 text-muted">
                    {t("contracts.not_found", "Contract not found")}
                  </div>
                ) : (
                  <>
                    <Card className="border-0 shadow-sm mb-3">
                      <CardBody className="p-4">
                        <div className="d-flex align-items-center mb-4">
                          <div
                            className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3"
                            style={{ width: "40px", height: "40px" }}
                          >
                            <i className="bx bx-file text-primary fs-5"></i>
                          </div>
                          <div>
                            <h5 className="mb-1 fw-bold">
                              {t("contracts.detail_modal.info_title", "Contract Information")}
                            </h5>
                            <p className="text-muted small mb-0">
                              {t("contracts.detail_modal.info_subtitle", "Complete contract specifications and details")}
                            </p>
                          </div>
                        </div>

                        <Row className="g-4">
                          <Col md={6}>
                            <div className="bg-light rounded p-3 h-100">
                              <h6 className="text-primary mb-3 fw-semibold">
                                <i className="bx bx-info-circle me-2"></i>
                                {t("contracts.detail_modal.contract_info", "Contract Information")}
                              </h6>
                              <div className="mb-3">
                                <label className="text-muted small">{t("contracts.contract_number_full", "Contract Number")}</label>
                                <div className="fw-medium">{data.contractNumber || "-"}</div>
                              </div>
                              <div className="mb-3">
                                <label className="text-muted small">{t("contracts.category", "Category")}</label>
                                <div className="fw-medium">{data.categoryName || "-"}</div>
                              </div>
                              <div className="mb-3">
                                <label className="text-muted small">{t("contracts.vendor", "Vendor")}</label>
                                <div className="fw-medium">{data.vendorName || "-"}</div>
                              </div>
                              <div>
                                <label className="text-muted small">{t("contracts.status", "Status")}</label>
                                <div>{badge && <Badge color={badge.color}>{t(badge.labelKey, badge.fallback)}</Badge>}</div>
                              </div>
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="bg-light rounded p-3 h-100">
                              <h6 className="text-success mb-3 fw-semibold">
                                <i className="bx bx-calendar me-2"></i>
                                {t("contracts.detail_modal.dates_financial", "Dates & Financial")}
                              </h6>
                              <div className="mb-3">
                                <label className="text-muted small">{t("contracts.start_date", "Start Date")}</label>
                                <div className="fw-medium">{formatDDMMYYYY(data.startDate) || "-"}</div>
                              </div>
                              <div className="mb-3">
                                <label className="text-muted small">{t("contracts.end_date", "End Date")}</label>
                                <div className="fw-medium">{formatDDMMYYYY(data.endDate) || "-"}</div>
                              </div>
                              <div className="mb-3">
                                <label className="text-muted small">{t("contracts.contract_value", "Contract Value")}</label>
                                <div className="fw-medium">
                                  {data.contractValue != null
                                    ? `${data.contractValue} ${orgCurrency}`.trim()
                                    : "-"}
                                </div>
                              </div>
                              {data.daysRemaining != null && (
                                <div className="mb-3">
                                  <label className="text-muted small">{t("contracts.days_remaining", "Days remaining")}</label>
                                  <div className="fw-medium">{data.daysRemaining}</div>
                                </div>
                              )}
                              <div>
                                <label className="text-muted small">{t("contracts.next_reminder", "Next renewal reminder")}</label>
                                <div className="fw-medium">
                                  {data.nextReminderDate
                                    ? formatDDMMYYYY(data.nextReminderDate)
                                    : t("contracts.no_upcoming_reminder", "No upcoming reminder")}
                                </div>
                                <small className="text-muted">
                                  {t("contracts.reminder_schedule_hint", "Admins are notified 90/60/30/7 days before and on the end date.")}
                                </small>
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>

                    {data.notes && (
                      <Card className="border-0 shadow-sm mb-3">
                        <CardBody className="p-4">
                          <h6 className="fw-semibold mb-3">
                            <i className="bx bx-note text-muted me-2"></i>
                            {t("contracts.notes", "Notes")}
                          </h6>
                          <p className="mb-0 text-muted" style={{ whiteSpace: "pre-wrap" }}>
                            {data.notes}
                          </p>
                        </CardBody>
                      </Card>
                    )}

                    {data.attachmentUrls && data.attachmentUrls.length > 0 && (
                      <Card className="border-0 shadow-sm">
                        <CardBody className="p-4">
                          <h6 className="fw-semibold mb-3">
                            <i className="bx bx-paperclip text-muted me-2"></i>
                            {t("shared.attachments", "Attachments")}
                          </h6>
                          <ul className="list-unstyled mb-0">
                            {data.attachmentUrls.map((url, idx) => (
                              <li key={idx} className="d-flex align-items-center mb-2">
                                <FeatherIcon icon="file-text" size={16} className="text-primary me-2" />
                                <a href={url} target="_blank" rel="noopener noreferrer">
                                  {decodeURIComponent(url.split("/").pop() || `file-${idx + 1}`)}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </CardBody>
                      </Card>
                    )}
                  </>
                )}
              </div>
            </TabPane>

            {/* Assets tab */}
            <TabPane tabId="assets">
              <div className="p-4">
                <Card className="border-0 shadow-sm">
                  <CardBody className="p-4">
                    <div className="d-flex align-items-center mb-4">
                      <div
                        className="bg-info bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3"
                        style={{ width: "40px", height: "40px" }}
                      >
                        <i className="bx bx-package text-info fs-5"></i>
                      </div>
                      <div>
                        <h5 className="mb-1 fw-bold">
                          {t("contracts.detail_modal.tab_assets", "Associated Assets")}
                        </h5>
                        <p className="text-muted small mb-0">
                          {t("contracts.detail_modal.assets_subtitle", {
                            count: assetsCount,
                            defaultValue: "{{count}} asset(s) covered by this contract",
                          })}
                        </p>
                      </div>
                    </div>

                    <AssetListTable
                      data={(data?.assets || []).map((a) => ({
                        ...a,
                        assetName: a.name || a.assetName,
                        currentLocation: a.currentLocationName || a.currentLocation,
                      }))}
                      loading={linkedAssetsLoading && (data?.assetIds?.length || 0) > 0}
                      loadingMessage={t("shared.loading_assets", "Loading assets...")}
                      emptyMessage={t("contracts.no_assets", "No assets attached")}
                      onAssetClick={(asset) => {
                        const full = linkedAssets.find((fa) => fa?.id === asset.id) || asset;
                        setAssetModal({ isOpen: true, userData: full });
                      }}
                    />
                  </CardBody>
                </Card>
              </div>
            </TabPane>
          </TabContent>
        </div>

        {/* Footer */}
        <div className="modal-footer border-0 pt-0">
          <button type="button" className="btn btn-secondary" onClick={toggle}>
            <FeatherIcon icon="x" size={14} className="me-1" />
            {t("shared.close", "Close")}
          </button>
        </div>
      </motion.div>

      {assetModal.isOpen && (
        <AssetModal
          isOpen={assetModal.isOpen}
          toggle={() => setAssetModal({ isOpen: false, userData: null })}
          userData={assetModal.userData}
          loading={false}
          formatCreatedDate={formatCreatedDate}
        />
      )}
    </Modal>
  );
};

export default ContractDetailModal;
